import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData, getNoSlip } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/Grid/SnGrid"; // 공통 그리드H
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import { showPopupModal } from "@widgets/CommonModal";
import SN_SAL_ISU_REG_003_POP_001 from "@menu/SAL/SN_SAL_ISU_REG_003/SN_SAL_ISU_REG_003_POP_001";
import SnButton from "@shared/components/SnButton";
import SnResizable from "@shared/components/SnResizable";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";

interface Props {
  Menuprops: menuInfo;
}
interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

// 출하반품등록
const SN_SAL_ISU_REG_003 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridApiRefH = useRef<any>(null); // 첫 번째  그리드 Reference 변수
  const gridApiRefL = useRef<any>(null); // 두 번째  그리드 Reference 변수
  const searchRef = useRef<any>(null);
  const today = new Date();
  const todayform = `${today.getFullYear()}${(today.getMonth() + 1)
    .toString()
    .padStart(2, "0")}${today.getDate().toString().padStart(2, "0")}`;
  const { userInfo } = global(); // Login userInfo 정보
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns1, setColums1] = useState<any[]>([]); // gridApiRefH 컬럼 설정 관련
  const [columns2, setColums2] = useState<any[]>([]); // gridApiRefL 컬럼 설정 관련
  const [dataSource1, setDataSource1] = useState<any[]>([]); // gridApiRefH datasource 관련
  const [dataSource2, setDataSource2] = useState<any>(); // gridApiRefL datasource 관련
  const [gridFocus, setGridFocus] = useState(false);
  //#endregion

  //#region panel 사이즈 관련
  const cd_menu = Menuprops.cd_menu || "SN_SAL_ISU_REG_003";
  const defaultSize = { panel1Height: "45.5%", panel2Height: "45.5%" };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize);

  const [selectedDocu, setSelectedDocu] = useState<string>("");
  const [selectedDtDocu, setSelectedDtDocu] = useState<string>("");

  useEffect(() => {
    const fetchform = async () => {
      setDataSource1([]);
      setDataSource2([]);
      try {
        const rtn: any = await getInitFormData();

        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        setFormData(rtn.formData); // 조회조건 초기값
        setColums1(rtn.grid.grid1); // H그리드 항목 설정
        setColums2(rtn.grid.grid2); // L그리드 항목 설정
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    gridApiRefH.current.selectAndFocusRow(0);
    setGridFocus(false);
  }, [gridFocus]);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridApiRefH.current.getChange();
    const modifiedRowsL = gridApiRefL.current.getChange();
    gridApiRefH.current.selectAndFocusRow(0);
    setGridFocus(true);
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        await onSave();
      }
    }
    try {
      const param = {
        id: "sn_sal_isu_reg_003_01",
        params: searchRef.current.getFormdata(),
      };
      setDataSource1([]);
      setDataSource2([]);
      const data: any = await fnGetDataSet(param);
      setDataSource1(data[0]);
      setDataSource2(data[1] || []);

      if (data[0] && data[0].length > 0) {
        // 1. 그리드 데이터가 바인딩되었음을 기다림
        await new Promise((resolve) => setTimeout(resolve, 50)); // 비동기 반영 대기 (최적화 가능)

        // 2. 첫 번째 행 선택 및 포커스 설정
        gridApiRefH.current.selectAndFocusRow(0);
      }

      gridApiRefH.current.resetUpdate();
      gridApiRefL.current.resetUpdate();
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onInsert = async () => {
    try {
      const newRow = {}; // 빈 객체
      gridApiRefH.current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onBeforeAddRowH = (newRow: RowData): boolean => {
    try {
      const mtlQtiohChanges = gridApiRefH.current.getChange().insert;

      if (mtlQtiohChanges.length > 0) return false;

      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: userInfo?.cd_biz,
        cd_biz_ref: userInfo?.cd_biz,
        cd_biz_isu: userInfo?.cd_biz,
        dt_req: todayform,
        cd_dept: userInfo?.cd_dept,
        nm_dept: userInfo?.nm_dept,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        cd_gp: userInfo?.cd_gp,
        dt_io: todayform,
        fg_trns: "1",
        st_req: "F",
        yn_autoprocess: "Y",
        no_io: "",
        fg_slip: "2",
        tp_slip: "2",
        cd_exch: "000",
        rt_exch: 1,
        fg_io: "2",
        cd_io: "250",
        cd_cust_dlv: "",
        am: 0,
        am_k: 0,
        am_vat: 0,
        am_supply: 0,
        qt_dummy01: 0,
        qt_dummy02: 0,
        fg_map: "1",
        fg_money: "1",
        no_hst: 0,
        no_req: "",
      };

      Object.assign(newRow, updatedRow); // 원본 객체 수정

      return true;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onAddRow = async () => {
    try {
      const newRow = {}; // 빈 객체
      gridApiRefL.current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onBeforeAddRowL = (newRow: RowData): boolean => {
    try {
      // gridH가 선택되지 않았을 경우 리턴
      if (
        !gridApiRefH ||
        gridApiRefH.current.api.getSelectedNodes().length === 0
      ) {
        alert("선택된 행이 없습니다.");
        return false;
      }
      const requiredValuesCheckH = gridApiRefH.current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }
      const allRowData: any[] = [];
      gridApiRefL.current.api.forEachNode((node: any) => {
        allRowData.push(node.data);
      });

      const selectedRows = gridApiRefH.current.api.getSelectedNodes();
      const maxNoLine =
        allRowData.length > 0
          ? Math.max(...allRowData.map((row: any) => row.no_line || 0))
          : 0;

      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: userInfo?.cd_biz,
        dt_dlv: todayform,
        no_line: maxNoLine + 1,
        no_line_lc: maxNoLine + 1,
        no_line_req: maxNoLine + 1,
        no_req: selectedRows[0].data.no_req,
        dt_req: selectedRows[0].data?.dt_req,
        no_io: selectedRows[0].data?.no_io,
        cd_io: selectedRows[0].data?.cd_io,
        fg_slip: selectedRows[0].data?.fg_slip,
        tp_slip: selectedRows[0].data?.tp_slip,
        fg_io: selectedRows[0].data?.fg_io,
        fg_money: selectedRows[0].data?.fg_money,
        fg_map: selectedRows[0].data?.fg_map,
        st_req: selectedRows[0].data?.st_req,
        yn_autoprocess: selectedRows[0].data?.yn_autoprocess,
        rt_exch: selectedRows[0].data?.rt_exch,
        fg_vat: "21",
        dt_io: selectedRows[0].data?.dt_io,
        fg_req: "100",
        qt: 0,
        qt_isu: 0,
        qt_inv: 0,
        qt_ep: 0,
        qt_cls: 0,
        qt_mgmt: 0,
        um: 0,
        am: 0,
        am_k: 0,
        qt_dummy01: 0,
        qt_dummy02: 0,
        qt_dummy03: 0,
        no_line_src_lot: 0,
        no_line_order: 0,
        um_k_pack: 0,
        um_supply_pack: 0,
        qt_isu_pack: 0,
        qt_cls_pack: 0,
        qt_dir: 0,
        rt_fat: 0,
        yn_insp_report: "N",
      };

      Object.assign(newRow, updatedRow); // 원본 객체 수정

      return true;
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = async () => {
    if (gridApiRefH.current?.api.getDisplayedRowCount() === 0) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    gridApiRefL.current?.deleteAllRows();
    gridApiRefH.current?.deleteSelectedRows();

    const realDeletedRows = gridApiRefH.current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      gridApiRefH.current.resetUpdate();
      gridApiRefL.current.resetUpdate();
      return;
    }

    await onSave();
  };

  const onDeleteRow = async () => {
    gridApiRefL.current?.deleteSelectedRows();
    await gridApiRefL.current.getFooterDataAsync();
    const footerData = gridApiRefL.current.footer;

    // gridApiRefH에서 focusedCell 찾기 및 setDataValue 작업
    const gridApiH = gridApiRefH.current.api;

    // 공통 메서드로 footerData 값을 focusedRow에 적용
    await setFooterDataToRow(gridApiH, footerData);
  };

  const onSave = async () => {
    try {
      gridApiRefH.current.api.stopEditing();
      gridApiRefL.current.api.stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH = gridApiRefH.current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return;
      }

      // L 필수값 체크
      const requiredValuesCheckL = gridApiRefL.current.requiredValuesCheck();
      if (!requiredValuesCheckL) {
        return;
      }

      const mtlQtiohChanges = gridApiRefH.current.getChange();
      const mtlQtiohanges = gridApiRefL.current.getChange();
      const headerInserts = mtlQtiohChanges.insert ?? [];
      const lineInserts = mtlQtiohanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog("alter", "저장에 실패했습니다.");
        return;
      }

      if (
        mtlQtiohChanges.modifiedRowcount === 0 &&
        mtlQtiohanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return;
      }

      //신규 등록된 수주건이 있는 경우 채번
      for (let row of headerInserts) {
        if (!row.no_req) {
          const cd_module: any = Menuprops.cd_module;
          try {
            // getNoSlip 호출
            const no_slip = await getNoSlip(
              cd_module,
              8, // no_line 값이 필요한 경우 사용
              "IRT", // cd_prefix 값이 필요하다면 여기에 적어주세요.
              row.dt_req, // 해당 날짜를 넘겨줘야 할 경우// cd_menu 값
              Menuprops.cd_menu || "SN_SAL_ISU_REG_003"
            );
            if (no_slip) {
              row.no_req = no_slip; // no_so 값을 추가
              row.no_io = no_slip; // no_so 값을 추가

              const gridApiH = gridApiRefH.current.api;
              const focusedCell = gridApiH.getFocusedCell();

              if (focusedCell) {
                const rowNode = gridApiH.getDisplayedRowAtIndex(
                  focusedCell.rowIndex
                );
                rowNode.setDataValue("no_req", no_slip);
                rowNode.setDataValue("no_io", no_slip);
              }

              for (let row of lineInserts) {
                if (!row.no_req) {
                  if (no_slip) {
                    row.no_req = no_slip;
                    row.no_io = no_slip; // no_so 값을 추가
                  }
                }
              }
              gridApiRefL.current.applyFilters({ no_req: no_slip });
              gridApiRefL.current.applyFilters({ no_io: no_slip });
            }
          } catch (error) {}
        }
      }

      const mtlQtoH: any[] = [];
      const mtlQtoL: any[] = [];

      for (const row of mtlQtiohChanges.insert) {
        const H = {
          cd_firm: userInfo.cd_firm,
          no_io: row.no_req,
          cd_biz: row.cd_biz,
          dt_io: todayform,
          rt_exch: row.rt_exch,
          cd_io: row.cd_io,
          fg_slip: row.fg_slip,
          tp_slip: row.tp_slip,
          fg_io: row.fg_io,
          fg_money: row.fg_money,
          cd_dept: row.cd_dept,
          cd_emp: row.cd_emp,
          cd_gp: row.cd_gp,
          yn_autoprocess: row.yn_autoprocess,
          am: 0,
          am_k: 0,
          am_vat: 0,
          am_supply: 0,
          fg_map: row.fg_map,
          am_card: 0,
          am_cash: 0,
          am_point: 0,
          am_coupon: 0,
          no_hst: 0,
        };
        mtlQtoH.push(H);
      }

      for (const row of mtlQtiohanges.insert) {
        const L = {
          cd_firm: userInfo.cd_firm,
          no_io: row.no_req,
          cd_biz: row.cd_biz,
          fg_slip: row.fg_slip,
          no_line: row.no_line,
          cd_wh: row.cd_wh,
          cd_io: row.cd_io,
          tp_slip: row.tp_slip,
          fg_io: row.gh_io,
          fg_money: row.fg_money,
          cd_item: row.cd_item,
          qt_good: 0,
          qt_bad: 0,
          qt: 0,
          qt_cls: row.qt_cls,
          um: 0,
          qm: 0,
          rt_exch: row.rt_exch,
          um_k: 0,
          am_k: 0,
          am_vat: 0,
          um_supply: row.um_supply,
          am_supply: row.am_supply,
          no_line_order: 0,
          no_line_req: 0,
          no_line_lc: 0,
          no_line_ip: 0,
          fg_map: row.fg_map,
          fg_req: row.fg_req,
          yn_req: row.yn_req,
          qt_rtnreq: 0,
          qt_rtn: 0,
          no_line_src: 0,
          qt_dummy01: 0,
          qt_dummy02: 0,
          yn_insp_report: row.yn_insu_report,
          am_supply_std: 0,
          am_dc: 0,
          rt_dc: row.rt_dc,
          um_pack: 0,
          um_supply_pack: 0,
          qt_good_pack: 0,
          qt_bad_pack: 0,
          qt_pack: 0,
          qt_cls_pack: 0,
          qt_rtnreq_pack: 0,
          qt_trn_pack: 0,
        };
        mtlQtoL.push(L);
      }

      // const SaveData = {
      //    mtl_qtioh: {insert : mtlQtoH},
      //    mtl_qtio: {insert : mtlQtoL},
      //   sal_isureqh: mtlQtiohChanges,
      //   sal_isureql: mtlQtiohanges,
      // };
      const SaveData = {
        mtl_qtioh: mtlQtiohChanges,
        mtl_qtio: mtlQtiohanges,
      };
      // 저장 API 호출
      await fnSave("SN_SAL_ISU_REG_003_SAVE_01", SaveData);

      // 성공적으로 저장된 후 초기화
      gridApiRefH.current?.resetUpdate();
      gridApiRefL.current?.resetUpdate();
    } catch (error: any) {}
  };

  const onGrid1BtnsClick = async (id: string) => {
    if (id == "aButton_app") {
      const mtlQtiohChanges = gridApiRefH.current.getChange().insert;
      if (mtlQtiohChanges.length === 0) {
        await showDialog("alter", "신규행을 추가하시기 바랍니다.");
        return;
      }

      const selectedFormData = searchRef.current.getFormdata();
      const selectedData = {
        cd_biz: selectedFormData.cd_biz,
        cd_cust: selectedFormData.cd_cust,
        dt_from: selectedFormData.dt_from,
        dt_to: selectedFormData.dt_to,
        fg_trns: selectedFormData.fg_trns,
      };

      const component = (
        <SN_SAL_ISU_REG_003_POP_001 selectedData={selectedData} />
      );
      const result = await showPopupModal({
        title: "반품적용",
        children: component,
      });
      if (!result || result === "close") return;

      const headerResult = result[0];
      const lineResult = result[1];

      const focusedCellH = gridApiRefH.current.api.getFocusedCell();

      if (focusedCellH) {
        const rowNode = gridApiRefH.current.api.getDisplayedRowAtIndex(
          focusedCellH.rowIndex
        );
        if (headerResult[0]) {
          headerResult[0].cd_biz &&
            gridApiRefH.current.setValue(
              rowNode,
              "cd_biz",
              headerResult[0].cd_biz
            );
          headerResult[0].dt_io &&
            gridApiRefH.current.setValue(rowNode, "dt_io", todayform);
          headerResult[0].cd_cust &&
            gridApiRefH.current.setValue(
              rowNode,
              "cd_cust",
              headerResult[0].cd_cust
            );
          headerResult[0].nm_cust &&
            gridApiRefH.current.setValue(
              rowNode,
              "nm_cust",
              headerResult[0].nm_cust
            );
          headerResult[0].nm_cust_sort &&
            gridApiRefH.current.setValue(
              rowNode,
              "nm_cust_sort",
              headerResult[0].nm_cust_sort
            );
          headerResult[0].fg_trns &&
            gridApiRefH.current.setValue(
              rowNode,
              "fg_trns",
              headerResult[0].fg_trns
            );
          headerResult[0].cd_exch &&
            gridApiRefH.current.setValue(
              rowNode,
              "cd_exch",
              headerResult[0].cd_exch
            );
          headerResult[0].cd_dept &&
            gridApiRefH.current.setValue(rowNode, "cd_dept", userInfo?.cd_dept);
          headerResult[0].cd_emp &&
            gridApiRefH.current.setValue(rowNode, "cd_emp", userInfo?.cd_emp);
          headerResult[0].nm_emp &&
            gridApiRefH.current.setValue(rowNode, "nm_emp", userInfo?.nm_emp);
          headerResult[0].cd_wh &&
            gridApiRefH.current.setValue(
              rowNode,
              "cd_wh",
              headerResult[0].cd_wh
            );
          headerResult[0].cd_gp &&
            gridApiRefH.current.setValue(
              rowNode,
              "cd_gp",
              headerResult[0].cd_gp
            );
          headerResult[0].fg_slip &&
            gridApiRefH.current.setValue(rowNode, "fg_slip", "2");
          headerResult[0].tp_slip &&
            gridApiRefH.current.setValue(rowNode, "tp_slip", "2");
          headerResult[0].fg_io &&
            gridApiRefH.current.setValue(rowNode, "fg_io", "2");
          headerResult[0].no_req &&
            gridApiRefH.current.setValue(
              rowNode,
              "no_req",
              headerResult[0].no_req
            );
          headerResult[0].fg_money &&
            gridApiRefH.current.setValue(
              rowNode,
              "fg_money",
              headerResult[0].fg_money
            );
          headerResult[0].dt_req &&
            gridApiRefH.current.setValue(rowNode, "dt_req", todayform);
          headerResult[0].cd_biz_isu &&
            gridApiRefH.current.setValue(
              rowNode,
              "cd_biz_isu",
              selectedData.cd_biz
            );
          headerResult[0].st_req &&
            gridApiRefH.current.setValue(rowNode, "st_req", "F");
        }
      }
      lineResult.map((result: any) => {
        if (headerResult[0]) {
          onAddRow();
          const rowCount = gridApiRefL.current.api.getDisplayedRowCount();
          const lastRowIndex = rowCount - 1;

          const lastRowNode =
            gridApiRefL.current.api.getDisplayedRowAtIndex(lastRowIndex);

          if (lastRowNode) {
            gridApiRefL.current.setValue(lastRowNode, "fg_vat", result.fg_vat);
            gridApiRefL.current.setValue(
              lastRowNode,
              "rt_exch",
              result.rt_exch
            );
            gridApiRefL.current.setValue(lastRowNode, "no_io", "");
            gridApiRefL.current.setValue(
              lastRowNode,
              "no_io_tmp",
              result.no_io
            );
            gridApiRefL.current.setValue(
              lastRowNode,
              "no_order",
              result.no_order
            );
            gridApiRefL.current.setValue(
              lastRowNode,
              "no_line_order",
              result.no_line_order
            );
            gridApiRefL.current.setValue(
              lastRowNode,
              "no_isu_src",
              result.no_io
            );
            gridApiRefL.current.setValue(
              lastRowNode,
              "no_line_isu_src",
              result.no_line
            );
            gridApiRefL.current.setValue(lastRowNode, "dt_io", todayform);
            gridApiRefL.current.setValue(lastRowNode, "dt_dlv", todayform);
            gridApiRefL.current.setValue(
              lastRowNode,
              "cd_item",
              result.cd_item
            );
            gridApiRefL.current.setValue(
              lastRowNode,
              "nm_item",
              result.nm_item
            );
            gridApiRefL.current.setValue(
              lastRowNode,
              "dc_item_spec",
              result.dc_item_spec
            );
            gridApiRefL.current.setValue(
              lastRowNode,
              "dc_item_unit",
              result.dc_item_unit
            );
            gridApiRefL.current.setValue(lastRowNode, "um", result.um);
            gridApiRefL.current.setValue(lastRowNode, "um_k", result.um_k);
            gridApiRefL.current.setValue(lastRowNode, "am", result.am);
            gridApiRefL.current.setValue(lastRowNode, "am_k", result.am_k);
            gridApiRefL.current.setValue(lastRowNode, "am_vat", result.am_vat);
            gridApiRefL.current.setValue(
              lastRowNode,
              "am_supply",
              result.am_supply
            );
            gridApiRefL.current.setValue(
              lastRowNode,
              "um_supply",
              result.um_supply
            );
            gridApiRefL.current.setValue(lastRowNode, "qt", result.qt_app);
            gridApiRefL.current.setValue(lastRowNode, "qt_good", result.qt_app);
            gridApiRefL.current.setValue(lastRowNode, "qt_bad", result.qt_bad);
            gridApiRefL.current.setValue(lastRowNode, "cd_pjt", result.cd_pjt);
            gridApiRefL.current.setValue(lastRowNode, "dc_rmk", result.dc_rmk);
            gridApiRefL.current.setValue(lastRowNode, "cd_wh", result.cd_wh);
            gridApiRefL.current.setValue(lastRowNode, "nm_wh", result.nm_wh);
            gridApiRefL.current.setValue(lastRowNode, "yn_lot", result.yn_lot);
            gridApiRefL.current.setValue(lastRowNode, "cd_io", result.cd_io);
            gridApiRefL.current.setValue(lastRowNode, "no_lot", result.no_lot);
            gridApiRefL.current.setValue(lastRowNode, "fg_slip", "2");
            gridApiRefL.current.setValue(lastRowNode, "tp_slip", "2");
            gridApiRefL.current.setValue(lastRowNode, "fg_io", "2");
            gridApiRefL.current.setValue(lastRowNode, "no_req", "");
            gridApiRefL.current.setValue(
              lastRowNode,
              "fg_money",
              result.fg_money
            );
            gridApiRefL.current.setValue(lastRowNode, "dt_req", todayform);
            gridApiRefL.current.setValue(
              lastRowNode,
              "cd_biz_isu",
              selectedData.cd_biz
            );
            gridApiRefL.current.setValue(lastRowNode, "st_req", "F");
          }
        }
      });
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      // 배열로 만들어서 필터링 함수 호출
      const no_io = selectedRow.no_io;
      const dt_io = selectedRow.dt_io || "";
      gridApiRefL.current.applyFilters({ no_io: no_io });
      gridApiRefL.current.selectAndFocusRow(0);
      setSelectedDocu(no_io);
      setSelectedDtDocu(dt_io);
    } else {
      return;
    }
  };

  // 계산식 함수
  const calculateAmounts = (row: any) => {
    const qt = row.qt ?? 0;
    const um = row.um ?? 0;
    row.am = qt * um;
    row.am_k = row.am;
    row.am_vat = row.am * 0.1;
    row.am_supply = row.am + row.am_vat;
  };

  const onCellValueChangedH = async (event: any) => {
    if (!event) return;
    const gridApiH = gridApiRefH.current.api;
    if (gridApiH) {
      const selectedDataH = gridApiH.getSelectedRows();
      if (selectedDataH.length > 0) {
        selectedDataH.forEach((row: any) => {
          if (row.cd_cust !== row._previousCdCust) {
            row.cd_cust_dlv = row.cd_cust; // cd_cust_dlv에 값을 넣음
          }
        });
      }
    }
  };

  const onCellValueChanged = async (event: any) => {
    if (!event) return;

    const updatedRow = event.data;

    const updatedColumnId = event.column.getColId();

    if (updatedColumnId === "qt" || updatedColumnId === "um") {
      calculateAmounts(updatedRow);
    }

    await gridApiRefL.current.getFooterDataAsync();
    const footerData = gridApiRefL.current.footer;

    const gridApiH = gridApiRefH.current.api;
    await setFooterDataToRow(gridApiH, footerData);
  };

  // footer에 집계된 내용 상단 그리드에 적용
  const setFooterDataToRow = async (gridApi: any, footerData: any) => {
    const focusedCell = gridApi.getFocusedCell();

    if (focusedCell) {
      const rowNode = gridApi.getDisplayedRowAtIndex(focusedCell.rowIndex);
      rowNode.setDataValue("am", footerData.am ?? 0);
      rowNode.setDataValue("am_k", footerData.am_k ?? 0);
      rowNode.setDataValue("am_vat", footerData.am_vat ?? 0);
      rowNode.setDataValue("am_supply", footerData.am_supply ?? 0);
    }
  };
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizable
          direction={"height"}
          minHeight={130}
          onResizeStop={onResizeStop}
          enable={{ bottom: true }}
          seq={1}
          size={sizes.panel1Height}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
        >
          <SnGrid
            // Pros로 받은 Menuprops
            seq={1} // Grid Seqno 우클릭 메뉴용
            ref={gridApiRefH} // Grid Name
            columnConfig={columns1} // 컬럼 설정 전달
            rowData={dataSource1} // Datasource
            isEditable={true} // 수정 가능여부
            rowSelection="single" // 로우 선택 모드 (단일)
            onBeforeAddRow={onBeforeAddRowH} // 행추가 전 유효성 체크 및 초기값 설정
            onFocusedRowChanged={onFocusedRowChanged}
            onCellValueChanged={onCellValueChangedH}
            title="반품정보" // 그리드 타이틀 정보
            header={
              <div className="flex justify-end">
                <SnButton
                  text={"출하적용"}
                  onClick={() => {
                    onGrid1BtnsClick("aButton_app");
                  }}
                />
              </div>
            }
            resizeController={{
              defaultSize: defaultSize,
              onResizeStop: onResizeStop,
            }}
          />
        </SnResizable>
        <SnResizable
          direction={"height"}
          minHeight={130}
          onResizeStop={onResizeStop}
          enable={{ bottom: true }}
          seq={2}
          size={sizes.panel2Height}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
        >
          <SnGrid
            // Pros로 받은 Menuprops
            seq={2} // Grid Seqno 우클릭 메뉴용
            ref={gridApiRefL} // Grid Name
            columnConfig={columns2} // 컬럼 설정 전달
            rowData={dataSource2} // Datasource
            enableExternalChart={false} // 하단 기본 차트 생성 여부
            isEditable={true} // 수정 가능여부
            rowSelection="multiple" // 로우 선택 모드(멀티)
            onBeforeAddRow={onBeforeAddRowL}
            onCellValueChanged={onCellValueChanged}
            onBtnDeleteClick={onGrid2BtnsClick}
            resizeController={{
              defaultSize: defaultSize,
              onResizeStop: onResizeStop,
            }}
          />
        </SnResizable>
        <SnFileUploadBox
          selectedDocu={selectedDocu}
          selectedLineDocu={0}
          selectedDtDocu={selectedDtDocu}
        />
      </div>
    </div>
  );
  //#endregion
});

export default SN_SAL_ISU_REG_003;
